<template>
  <el-form ref="form" :model="localData" :rules="rules" label-width="160px" size="small">
    <el-divider>经营信息</el-divider>

    <el-form-item label="商户简称" prop="merchantShortName">
      <el-input v-model="localData.merchantShortName" placeholder="请填写商户简称" maxlength="50" />
      <div class="form-tip">将展示在微信支付账单详情页</div>
    </el-form-item>

    <el-form-item label="客服电话" prop="servicePhone">
      <el-input v-model="localData.servicePhone" placeholder="请填写客服电话" maxlength="20" />
    </el-form-item>

    <el-form-item label="经营场景" prop="salesSceneType">
      <el-select v-model="localData.salesSceneType" placeholder="请选择经营场景" style="width:100%">
        <el-option label="线下门店" value="SALES_SCENES_STORE" />
        <el-option label="小程序" value="SALES_SCENES_MINI_PROGRAM" />
        <el-option label="公众号" value="SALES_SCENES_MP" />
        <el-option label="网站" value="SALES_SCENES_WEB" />
        <el-option label="APP" value="SALES_SCENES_APP" />
      </el-select>
    </el-form-item>

    <el-form-item label="经营描述" prop="businessDesc">
      <el-input v-model="localData.businessDesc" type="textarea" :rows="3" placeholder="请描述主要经营业务，如：主营川菜、火锅，兼营外卖配送" maxlength="200" />
    </el-form-item>

    <template v-if="localData.salesSceneType === 'SALES_SCENES_STORE'">
      <el-divider>线下场所信息</el-divider>

      <el-form-item label="所在地区" prop="bizAddressCode">
        <el-cascader
            v-model="bizAddressCodeArr"
            :options="addressOptions"
            :props="{ value: 'value', label: 'label', children: 'children' }"
            placeholder="请选择省/市/区"
            style="width:100%"
            clearable
            @change="handleAddressChange"
        />
      </el-form-item>

      <!-- ★★★ 新增：场所详细地址（独立于营业执照地址）★★★ -->
      <el-form-item label="场所详细地址" prop="storeAddress">
        <el-input v-model="localData.storeAddress" placeholder="请填写详细的门店地址，如：XX市XX路XX号XX楼宇" maxlength="200" />
        <div class="form-tip">请填写详细的门店地址，需包含省市区街道门牌号信息</div>
      </el-form-item>

      <el-divider>门店照片</el-divider>

      <el-form-item label="门店入口图" prop="storeEntranceImageId">
        <div class="upload-row">
          <el-input v-model="localData.storeEntranceUrl" placeholder="请点击选择图片按钮选择图片" readonly />
          <el-button type="primary" @click="openImageSelector('storeEntrance')">选择图片</el-button>
        </div>
        <div v-if="localData.storeEntranceUrl" class="image-preview">
          <el-image :src="localData.storeEntranceUrl" class="thumb" fit="cover" />
        </div>
        <div v-if="localData.storeEntranceMediaId" class="media-id-info">
          <span class="media-id-label">微信MediaId:</span>
          <span class="media-id-value">{{ localData.storeEntranceMediaId }}</span>
        </div>
      </el-form-item>

      <el-form-item label="店内环境图" prop="storeInteriorImageId">
        <div class="upload-row">
          <el-input v-model="localData.storeInteriorUrl" placeholder="请点击选择图片按钮选择图片" readonly />
          <el-button type="primary" @click="openImageSelector('storeInterior')">选择图片</el-button>
        </div>
        <div v-if="localData.storeInteriorUrl" class="image-preview">
          <el-image :src="localData.storeInteriorUrl" class="thumb" fit="cover" />
        </div>
        <div v-if="localData.storeInteriorMediaId" class="media-id-info">
          <span class="media-id-label">微信MediaId:</span>
          <span class="media-id-value">{{ localData.storeInteriorMediaId }}</span>
        </div>
      </el-form-item>
    </template>

    <div class="module-footer">
      <el-button type="primary" @click="submitModule" :loading="saving">保 存</el-button>
    </div>

    <!-- 图片选择器 -->
    <image-selector
        v-model="showImageSelector"
        :max-count="1"
        @select="handleImageSelect"
    />
  </el-form>
</template>

<script>
import api from '@/utils/api'
import ImageSelector from '@/components/ImageSelector'
import { addressCodeOptions } from '@/utils/addressCodeOptions'

export default {
  name: 'BusinessInfo',
  components: { ImageSelector },
  props: {
    data: { type: Object, default: () => ({}) },
    applyId: { type: Number, default: null },
    applyNo: { type: String, default: '' },
    merchantId: { type: Number, default: null }
  },
  data() {
    return {
      saving: false,
      showImageSelector: false,
      currentUploadField: '',
      addressOptions: addressCodeOptions,
      bizAddressCodeArr: [],
      localData: {
        merchantShortName: '',
        servicePhone: '',
        salesSceneType: '',
        businessDesc: '',
        bizAddressCode: '',
        storeAddress: '',  // ★★★ 新增：线下场所地址 ★★★
        storeEntranceUrl: '',
        storeEntranceMediaId: '',
        storeEntranceImageId: null,
        storeInteriorUrl: '',
        storeInteriorMediaId: '',
        storeInteriorImageId: null
      },
      rules: {
        merchantShortName: [
          { required: true, message: '请输入商户简称', trigger: 'blur' },
          { max: 50, message: '不能超过50个字符', trigger: 'blur' }
        ],
        servicePhone: [{ required: true, message: '请输入客服电话', trigger: 'blur' }],
        salesSceneType: [{ required: true, message: '请选择经营场景', trigger: 'change' }]
      }
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.localData = {
            merchantShortName: val.merchantShortName !== undefined ? val.merchantShortName : this.localData.merchantShortName,
            servicePhone: val.servicePhone !== undefined ? val.servicePhone : this.localData.servicePhone,
            salesSceneType: val.salesSceneType !== undefined ? val.salesSceneType : this.localData.salesSceneType,
            businessDesc: val.businessDesc !== undefined ? val.businessDesc : this.localData.businessDesc,
            bizAddressCode: val.bizAddressCode !== undefined ? val.bizAddressCode : this.localData.bizAddressCode,
            // ★★★ 新增 ★★★
            storeAddress: val.storeAddress !== undefined ? val.storeAddress : this.localData.storeAddress,
            storeEntranceUrl: val.storeEntranceUrl !== undefined ? val.storeEntranceUrl : this.localData.storeEntranceUrl,
            storeEntranceMediaId: val.storeEntranceMediaId !== undefined ? val.storeEntranceMediaId : this.localData.storeEntranceMediaId,
            storeEntranceImageId: val.storeEntranceImageId !== undefined ? val.storeEntranceImageId : this.localData.storeEntranceImageId,
            storeInteriorUrl: val.storeInteriorUrl !== undefined ? val.storeInteriorUrl : this.localData.storeInteriorUrl,
            storeInteriorMediaId: val.storeInteriorMediaId !== undefined ? val.storeInteriorMediaId : this.localData.storeInteriorMediaId,
            storeInteriorImageId: val.storeInteriorImageId !== undefined ? val.storeInteriorImageId : this.localData.storeInteriorImageId
          }
          if (val.bizAddressCode && this.bizAddressCodeArr.length === 0) {
            this.findAddressPath(val.bizAddressCode)
          }
        }
      }
    },
    'localData.salesSceneType'(val) {
      const isStore = val === 'SALES_SCENES_STORE'
      this.rules.bizAddressCode = isStore ? [{ required: true, message: '请选择所在地区', trigger: 'change' }] : [{ required: false }]
      // ★★★ 新增：场所地址校验 ★★★
      this.rules.storeAddress = isStore ? [{ required: true, message: '请输入场所详细地址', trigger: 'blur' }] : [{ required: false }]
      this.rules.storeEntranceImageId = isStore ? [{ required: true, message: '请上传门店入口图', trigger: 'change' }] : [{ required: false }]
      this.rules.storeInteriorImageId = isStore ? [{ required: true, message: '请上传店内环境图', trigger: 'change' }] : [{ required: false }]
      this.$nextTick(() => { if (this.$refs.form) this.$refs.form.clearValidate() })
    }
  },
  methods: {
    // 根据编码回显级联路径
    findAddressPath(code) {
      if (!code) return
      for (const p of this.addressOptions) {
        for (const c of (p.children || [])) {
          if (c.children) {
            for (const d of c.children) {
              if (d.value === code) {
                this.bizAddressCodeArr = [p.value, c.value, d.value]
                return
              }
            }
          } else {
            if (c.value === code) {
              this.bizAddressCodeArr = [p.value, c.value]
              return
            }
          }
        }
      }
    },

    handleAddressChange(val) {
      if (val && val.length > 0) {
        this.localData.bizAddressCode = val[val.length - 1]
      } else {
        this.localData.bizAddressCode = ''
      }
    },

    openImageSelector(field) {
      this.currentUploadField = field
      this.showImageSelector = true
    },

    async handleImageSelect(images) {
      if (!images || images.length === 0) return

      const image = images[0]
      const imageUrl = image.url
      const imageId = image.id

      const urlField = this.getUrlField(this.currentUploadField)
      const idField = this.getIdField(this.currentUploadField)
      const mediaIdField = this.getMediaIdField(this.currentUploadField)

      if (urlField) this.$set(this.localData, urlField, imageUrl)
      if (idField) this.$set(this.localData, idField, imageId)

      const purpose = this.getPurpose(this.currentUploadField)
      if (purpose && imageUrl) {
        try {
          const res = await api.subMerchant_fetchMediaId({ imageUrl, purpose })
          if (res?.data?.code === 200 || res?.data?.success) {
            const mediaId = res.data.data?.media_id || res.data.data?.mediaId
            if (mediaId && mediaIdField) {
              this.$set(this.localData, mediaIdField, mediaId)
            }
          }
        } catch (e) {
          console.error('获取微信MediaId失败:', e)
        }
      }
    },

    getUrlField(field) {
      return { 'storeEntrance': 'storeEntranceUrl', 'storeInterior': 'storeInteriorUrl' }[field] || ''
    },

    getIdField(field) {
      return { 'storeEntrance': 'storeEntranceImageId', 'storeInterior': 'storeInteriorImageId' }[field] || ''
    },

    getMediaIdField(field) {
      return { 'storeEntrance': 'storeEntranceMediaId', 'storeInterior': 'storeInteriorMediaId' }[field] || ''
    },

    getPurpose(field) {
      return { 'storeEntrance': 'store_entrance_pic', 'storeInterior': 'indoor_pic' }[field] || ''
    },

    validate(cb) {
      if (this.$refs.form) this.$refs.form.validate(cb)
      else cb(false)
    },

    async submitModule() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return this.$message.warning('请完善必填信息')
        this.saving = true
        try {
          const params = {
            id: this.applyId,
            applyNo: this.applyNo,
            merchantId: this.merchantId,
            rejectReason: 'business',
            merchantShortName: this.localData.merchantShortName,
            servicePhone: this.localData.servicePhone,
            salesSceneType: this.localData.salesSceneType,
            businessDesc: this.localData.businessDesc,
            bizAddressCode: this.localData.bizAddressCode,
            // ★★★ 新增：场所详细地址 ★★★
            storeAddress: this.localData.storeAddress,
            storeEntranceMediaId: this.localData.storeEntranceMediaId,
            storeEntranceImageId: this.localData.storeEntranceImageId,
            storeInteriorMediaId: this.localData.storeInteriorMediaId,
            storeInteriorImageId: this.localData.storeInteriorImageId
          }
          console.log('========== [BusinessInfo] 提交经营信息 ==========')
          console.log('提交数据:', JSON.stringify(params, null, 2))
          const res = await api.subMerchant_saveApply(params)
          console.log('[BusinessInfo] 响应:', res)
          if (res?.data?.success) {
            this.$emit('saved', res.data.data)
          } else {
            this.$message.error(res?.data?.message || '保存失败')
          }
        } catch (e) {
          console.error('[BusinessInfo] 保存失败:', e)
          this.$message.error('保存失败')
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.upload-row { display: flex; gap: 8px; }
.upload-row .el-input { flex: 1; }
.image-preview { margin-top: 8px; }
.thumb { width: 100px; height: 100px; border: 1px solid #eee; border-radius: 4px; object-fit: cover; }
.media-id-info { margin-top: 8px; font-size: 12px; color: #606266; }
.media-id-label { font-weight: 500; color: #909399; }
.media-id-value { margin-left: 8px; color: #67c23a; word-break: break-all; }
.form-tip { font-size: 12px; color: #909399; margin-top: 4px; }
.module-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #ebeef5; text-align: right; }
</style>